/**
 * OptionsWebSocketContext - Глобальный провайдер для WebSocket опционов
 * ЗАЧЕМ: Единый источник live bid/ask для всех опционов выбранной экспирации
 * Затрагивает: OptionsBoard (доска опционов) + OptionsCalculatorBasic (таблица выбранных опционов)
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocketControl } from './WebSocketContext';
import { WS_BASE_URL } from '../config/api';

const OptionsWebSocketContext = createContext();

export const useOptionsQuotes = () => {
  const context = useContext(OptionsWebSocketContext);
  if (!context) {
    throw new Error('useOptionsQuotes must be used within OptionsWebSocketProvider');
  }
  return context;
};

export const OptionsWebSocketProvider = ({ children }) => {
  // Глобальное управление WebSocket (Play/Pause кнопка)
  const { isWebSocketEnabled } = useWebSocketControl();
  
  const [rawQuotes, setRawQuotes] = useState(new Map()); // Map<symbol, {bid, ask, last, volume, timestamp}>
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Флаг паузы (подписки отключены, но WS открыт)
  const [quotesCount, setQuotesCount] = useState(0);
  const [subscribedSymbols, setSubscribedSymbols] = useState([]);
  const [atmPrice, setAtmPrice] = useState(null);
  const [dataMode, setDataMode] = useState(null); // 'live' | 'historical' | null
  
  const wsRef = useRef(null);
  const subscriptionParamsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const isConnectingRef = useRef(false); // Защита от повторных подключений
  const lastCloseTimeRef = useRef(0); // Время последнего закрытия
  const pausedSymbolsRef = useRef([]); // Сохранённые символы при паузе
  const isWebSocketEnabledRef = useRef(true); // Ref для проверки паузы в handleMessage
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 10; // Достаточно для production (перезапуски backend)

  const connect = () => {
    // Защита от бесконечного цикла подключений
    if (wsRef.current || isConnectingRef.current) return;
    
    // Cooldown 3 секунды после закрытия
    const timeSinceClose = Date.now() - lastCloseTimeRef.current;
    if (lastCloseTimeRef.current > 0 && timeSinceClose < 3000) {
      console.log(`⏳ [Options WS] Cooldown: ${Math.ceil((3000 - timeSinceClose) / 1000)}s`);
      return;
    }
    
    isConnectingRef.current = true;

    const ws = new WebSocket(`${WS_BASE_URL}/api/databento/ws/quotes`);
    wsRef.current = ws;

    ws.onopen = () => {
      isConnectingRef.current = false;
      setIsConnected(true);
      reconnectAttemptsRef.current = 0; // Сброс счётчика при успешном подключении

      const authMsg = {
        action: 'auth',
        token: 'XoMSGltU1i5Co0eoO0LMYbDY8YM4_2WLYXrCUNxsdQU'
      };
      ws.send(JSON.stringify(authMsg));
      
      // Ping каждые 30 секунд для поддержания соединения
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch (e) {
        console.error('❌ Parse error:', e.message);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    ws.onclose = (event) => {
      // Запоминаем время закрытия для cooldown
      lastCloseTimeRef.current = Date.now();
      isConnectingRef.current = false;
      
      // Очищаем ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      wsRef.current = null;
      setIsConnected(false);
      setIsAuthenticated(false);
      setIsSubscribed(false);
      setSubscribedSymbols([]);
      
      // Reconnect логика с exponential backoff
      const shouldReconnect = isWebSocketEnabledRef.current && reconnectAttemptsRef.current < maxReconnectAttempts;
      
      if (shouldReconnect) {
        // Код 1012 = Service Restart — reconnect сразу без задержки
        // ЗАЧЕМ: При перезапуске backend пользователь не должен ждать
        const isServiceRestart = event.code === 1012;
        const delay = isServiceRestart ? 1000 : Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current), 60000);
        
        console.log(`🔄 [Options WS] Reconnecting in ${delay}ms (code: ${event.code}, attempt: ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connect();
        }, delay);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.error('❌ [Options WS] Max reconnect attempts reached');
      }
    };
  };

  // Отписка от всех опционов конкретного underlying
  // ЗАЧЕМ: Очистить старые подписки перед подпиской на новые
  const unsubscribeOptions = (underlying) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [Options WS] Cannot unsubscribe - not connected');
      return;
    }

    const msg = {
      action: 'unsubscribe_options',
      underlying: underlying
    };

    wsRef.current.send(JSON.stringify(msg));
    console.log(`📤 [Options WS] Unsubscribed from: ${underlying}`);
  };

  const subscribeOptions = (underlying, expiration, atmRangePercent = 20) => {
    if (!wsRef.current || !isAuthenticated) {
      console.error('❌ Not connected or not authenticated');
      return;
    }

    // КРИТИЧНО: Сначала отписываемся от старого underlying (если есть)
    // ЗАЧЕМ: Предотвращаем накопление подписок при смене контракта/экспирации
    if (subscriptionParamsRef.current?.underlying) {
      unsubscribeOptions(subscriptionParamsRef.current.underlying);
    }

    // Очищаем локальный state ПЕРЕД новой подпиской
    setRawQuotes(new Map());
    setSubscribedSymbols([]);
    setIsSubscribed(false);
    setQuotesCount(0);

    const msg = {
      action: 'subscribe_options',
      underlying: underlying,
      expiration: expiration,
      atm_range_percent: atmRangePercent
    };

    wsRef.current.send(JSON.stringify(msg));
    console.log(`📤 [Options WS] Subscribed: ${underlying} @ ${expiration}`);
    
    subscriptionParamsRef.current = { underlying, expiration, atmRangePercent };
  };

  const disconnect = () => {
    if (wsRef.current) {
      // Очищаем ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
      setIsAuthenticated(false);
      setIsSubscribed(false);
      setRawQuotes(new Map());
      setSubscribedSymbols([]);
      setQuotesCount(0);
    }
  };

  // Синхронизируем ref с глобальным состоянием паузы
  useEffect(() => {
    isWebSocketEnabledRef.current = isWebSocketEnabled;
    if (!isWebSocketEnabled) {
      console.log('⏸️ [Options WS] PAUSE - котировки будут игнорироваться');
    } else {
      console.log('▶️ [Options WS] PLAY - котировки обрабатываются');
    }
  }, [isWebSocketEnabled]);

  const handleMessage = (data) => {
    const type = data.type;

    if (type === 'welcome') {
      setIsAuthenticated(true);
    }
    else if (type === 'options_subscribed') {
      const count = data.symbols_count || 0;
      const atmPrice = data.atm_price || 0;
      const symbols = data.symbols || [];

      console.log(`✅ [Options WS] ${count} options subscribed (ATM: $${atmPrice})`);
      setIsSubscribed(true);
      setAtmPrice(atmPrice);
      setSubscribedSymbols(symbols);

      const newQuotes = new Map();
      symbols.forEach(symbol => {
        newQuotes.set(symbol, {
          symbol: symbol,
          bid: null,
          ask: null,
          last: null,
          volume: null,
          timestamp: null
        });
      });
      setRawQuotes(newQuotes);
    }
    else if (type === 'quote') {
      // PAUSE: игнорируем котировки когда на паузе
      if (!isWebSocketEnabledRef.current) {
        return;
      }
      
      // Фильтруем только опционы, игнорируем фьючерсы
      if (data.instrument_type !== 'option') {
        return;
      }
      
      const symbol = data.symbol;
      setQuotesCount(prev => prev + 1);
      
      // Устанавливаем dataMode из приходящих данных
      if (data.data_mode === 'live') {
        setDataMode('live');
      }

      setRawQuotes(prev => {
        const updated = new Map(prev);
        updated.set(symbol, {
          symbol: symbol,
          bid: data.bid,
          ask: data.ask,
          last: data.last,
          volume: data.volume,
          timestamp: new Date(),
          receivedAt: new Date().toISOString()
        });
        return updated;
      });
    }
    else if (type === 'error') {
      console.error(`❌ Error: ${data.message}`);
    }
    else if (type === 'heartbeat' || type === 'pong') {
      // Тихо игнорируем heartbeat и pong
    }
    else if (type === 'unsubscribed') {
      // Подтверждение отписки при паузе
      console.log('⏸️ [Options WS] Unsubscribed (paused)');
    }
    else {
      // Игнорируем неизвестные типы
    }
  };

  // Добавляем isConnecting для совместимости с TopNav
  const isConnecting = isConnectingRef.current;

  const value = {
    rawQuotes,
    isConnected,
    isConnecting,
    isAuthenticated,
    isSubscribed,
    isPaused,
    quotesCount,
    subscribedSymbols,
    atmPrice,
    dataMode,
    connect,
    disconnect,
    subscribeOptions,
    unsubscribeOptions
  };

  return (
    <OptionsWebSocketContext.Provider value={value}>
      {children}
    </OptionsWebSocketContext.Provider>
  );
};
